import * as methods from 'api/core/actions';
import * as z from 'zod';
import { projectSuggestionSchema } from './projectSuggestions.schema';

/**
 * Read contract for TT-6058 agent sessions. The Rails side is internal-only
 * (first-party apps, `agent_tracking` feature) and returns one row per agent
 * conversation with the activity periods that fall inside the requested day
 * window nested under `segments`.
 */
/**
 * Tool id as uploaded by the tracker: free-form lowercase snake_case (max 32
 * chars) so a new tool needs no release here. `provider_name` carries the
 * display name; the frontend only keys icons by well-known ids.
 */
export const agentProviderSchema = z.string().min(1).max(32);
export type AgentProvider = z.infer<typeof agentProviderSchema>;

/**
 * Who besides the owner sees a session. `published`: people who can see the owner's work.
 * `shared`: only people who can see hours on the project a period is on. `private`:
 * nobody. Required, not defaulted: a server that omits it does not enforce sharing, so
 * showing the session as private would be false. Failing the parse is the safe outcome.
 * The server also sends it per period; the UI only needs the session's.
 */
const agentVisibilitySchema = z.enum(['published', 'shared', 'private']);
export type AgentVisibility = z.infer<typeof agentVisibilitySchema>;

const tokenUsageSchema = z.record(z.string(), z.record(z.string(), z.number()));

const agentSessionSegmentSchema = z.object({
  id: z.number(),
  segment_key: z.string(),
  day: z.string(),
  started_at: z.string(),
  ended_at: z.string(),
  metric_availability: z.record(z.string(), z.boolean()).optional(),
  active_seconds: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  total_tokens: z.number(),
  token_usage: tokenUsageSchema,
  speed_unknown_usage: tokenUsageSchema.nullable().optional(),
  speed_overrides: z.record(z.string(), z.enum(['standard', 'priority', 'ultrafast'])).optional(),
  /**
   * Standard API list-price estimate, fixed by Rails when it stored the period.
   * Null when the period could not be priced or the viewer may not see cost.
   */
  estimated_cost: z.number().nullish(),
  currency_code: z.string().nullish(),
  /** Rate card that priced the period: the date of its newest price change. */
  pricing_version: z.string().nullish(),
  tool_counts: z.record(z.string(), z.number()),
  summary: z.string().nullable(),
  project_id: z.number().nullable(),
  project_assignment_source: z.enum(['manual', 'inherited', 'automatic']).nullable().optional(),
  project_suggestion: projectSuggestionSchema.nullable().optional().catch(null),
  project_prediction_state: z.string().nullable().optional(),
  prediction_ready: z.boolean().optional(),
  label_ids: z.array(z.number()).optional(),
  resource_id: z.string().nullable().optional(),
  /**
   * Whether the viewer may change this period's project, tags and linked work: the
   * owner always; someone who may edit the owner's hours on what they can see (TT-6346).
   * Absent from older Rails, which let only the owner edit.
   */
  can_edit: z.boolean().optional(),
  /**
   * A colleague's period: the name and color of its project, for a viewer who may not be
   * on it and so can't load it (TT-6346). Absent on the viewer's own periods.
   */
  project_name: z.string().nullish(),
  project_color: z.string().nullish(),
});

const agentSessionSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  provider: agentProviderSchema,
  provider_name: z.string(),
  metrics_available: z.boolean().optional(),
  provider_session_id: z.string(),
  title: z.string().nullable(),
  default_project_id: z.number().nullable().optional(),
  folder_name: z.string().nullable(),
  git_remote: z.string().nullable(),
  git_branch: z.string().nullable(),
  started_at: z.string().nullable(),
  ended_at: z.string().nullable(),
  active_seconds: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  total_tokens: z.number(),
  token_usage: tokenUsageSchema,
  speed_unknown_usage: tokenUsageSchema.nullable().optional(),
  speed_overrides: z.record(z.string(), z.enum(['standard', 'priority', 'ultrafast'])).optional(),
  visibility: agentVisibilitySchema,
  /** Whether the viewer may rename the session (owner, or their admin or team lead). */
  can_edit: z.boolean().optional(),
  segments: z.array(agentSessionSegmentSchema),
  window: z.object({
    from: z.string().nullable(),
    to: z.string().nullable(),
    segment_count: z.number(),
    active_seconds: z.number(),
    input_tokens: z.number(),
    output_tokens: z.number(),
    cache_read_tokens: z.number(),
    cache_write_tokens: z.number(),
  }),
});

const agentSessionsResponseSchema = z.object({
  sessions: z.array(agentSessionSchema),
  has_uploaded_sessions: z.boolean().nullish(),
  period: z.object({ from: z.string(), to: z.string() }),
  truncated: z.boolean(),
  limit: z.number(),
  next_cursor: z.string().nullable().optional(),
});

export type AgentSessionSegment = z.infer<typeof agentSessionSegmentSchema>;
export type AgentSession = z.infer<typeof agentSessionSchema>;
export type AgentSessionsResponse = z.infer<typeof agentSessionsResponseSchema>;

export interface AgentSessionsQuery {
  pageSize?: number;
  cursor?: string;
  /** Window start, ISO date (inclusive). */
  from: string;
  /** Window end, ISO date (inclusive). */
  to: string;
  /**
   * `company`: every session in the workspace with a period the viewer may see, with
   * only those periods and with session context (title, cost) only where the viewer
   * may see it. Omitted: the signed-in user's own sessions.
   */
  scope?: 'company';
  /**
   * One colleague's sessions: only the periods the viewer may see, each with can_edit.
   * Omitted or the viewer's own id: their own sessions.
   */
  userId?: number;
  /**
   * Only these sessions, unpaginated (at most 100): a live page reading back the
   * sessions a change event named. A session missing from the response has no
   * period the viewer may see in the window.
   */
  sessionIds?: number[];
}

export class AgentSessionsResponseShapeError extends Error {
  constructor(detail: string) {
    super(`Agent sessions response did not match the expected shape (${detail})`);
    this.name = 'AgentSessionsResponseShapeError';
  }
}

export function parseAgentSessionsResponse(raw: unknown): AgentSessionsResponse {
  const parsed = agentSessionsResponseSchema.safeParse(raw);
  if (parsed.success) return parsed.data;

  // eslint-disable-next-line no-console
  console.error('Agent sessions: unexpected response shape', { raw, issues: parsed.error.issues });
  const [first] = parsed.error.issues;
  const path = first?.path.join('.');
  throw new AgentSessionsResponseShapeError(
    first ? `${path ? `${path}: ` : ''}${first.message}` : 'unrecognized payload'
  );
}

/**
 * The signed-in user's agent sessions with their activity periods in the window.
 * GET /:account_id/agent_sessions?from=...&to=...
 */
export async function getAgentSessions(
  query: AgentSessionsQuery,
  fetchPage: (path: string) => Promise<unknown> = path => methods.Get(path)
): Promise<AgentSessionsResponse> {
  const params = new URLSearchParams({
    from: query.from,
    to: query.to,
    compact_suggestions: 'true',
  });
  if (query.scope) params.set('scope', query.scope);
  if (query.userId) params.set('user_id', String(query.userId));
  if (query.pageSize) params.set('page_size', String(query.pageSize));
  if (query.cursor) params.set('cursor', query.cursor);
  for (const id of query.sessionIds ?? []) params.append('session_ids[]', String(id));
  return parseAgentSessionsResponse(await fetchPage(`agent_sessions?${params}`));
}

export type AgentSpeed = 'standard' | 'priority' | 'ultrafast';

export interface AssignProjectBody {
  speed_override?: { model: string; tier: AgentSpeed | null };
  /** User-owned title; preserved across tracker uploads. */
  title?: string;
  /** Project to assign, or null to clear. */
  project_id?: number | null;
  /** Replacement tags; omit to preserve, [] to clear. */
  label_ids?: number[];
  /** TIC reference, matching AutoSheet; null clears. */
  resource_id?: string | null;
  /** Periods to change; omit to change every period of the session. */
  segment_ids?: number[];
}

const assignProjectResponseSchema = z.object({
  session: agentSessionSchema,
  updated_count: z.number(),
});
export type AssignProjectResponse = z.infer<typeof assignProjectResponseSchema>;

/**
 * Review flow: set or clear the project on some or all periods of the signed-in
 * user's own session. Web host only; the tracker never writes project_id.
 * PATCH /:account_id/agent_sessions/:id/assign
 */
export async function assignAgentSessionProject(
  sessionId: number,
  body: AssignProjectBody
): Promise<AssignProjectResponse> {
  const raw = await methods.Patch(
    `agent_sessions/${sessionId}/assign?compact_suggestions=true`,
    body
  );
  const parsed = assignProjectResponseSchema.safeParse(raw);
  if (parsed.success) return parsed.data;

  // eslint-disable-next-line no-console
  console.error('Agent sessions: unexpected assign response shape', {
    raw,
    issues: parsed.error.issues,
  });
  const [first] = parsed.error.issues;
  throw new AgentSessionsResponseShapeError(
    first ? `${first.path.join('.')}: ${first.message}` : 'unrecognized payload'
  );
}

/** Remove the entire session, including periods outside the current view. */
export async function deleteAgentSession(sessionId: number): Promise<void> {
  await methods.Delete(`agent_sessions/${sessionId}`);
}

/** Complete owned session, including periods outside the current filters. */
export async function getAgentSession(sessionId: number): Promise<AgentSession> {
  const response = z
    .object({ session: agentSessionSchema })
    .parse(await methods.Get(`agent_sessions/${sessionId}?compact_suggestions=true`));
  return response.session;
}

/**
 * Owner only: publish a session, or make it private (its projects stay, and
 * putting work on a project shares it again). Covers the current and future
 * periods of the session.
 * PATCH /:account_id/agent_sessions/:id/publication
 */
export async function setAgentSessionPublished(
  sessionId: number,
  published: boolean
): Promise<AgentSession> {
  const response = z
    .object({ session: agentSessionSchema })
    .parse(await methods.Patch(`agent_sessions/${sessionId}/publication`, { published }));
  return response.session;
}

const publishAllResponseSchema = z.object({
  published_count: z.number(),
  published_at: z.string(),
});
export type PublishAllResponse = z.infer<typeof publishAllResponseSchema>;

export interface PublishAllBody {
  /** Also publish the sessions the owner made private; they are skipped without it. */
  include_kept_private?: boolean;
}

/**
 * Publish every unpublished session of the signed-in user in this workspace.
 * POST /:account_id/agent_sessions/publish_all
 */
export async function publishAllAgentSessions(body?: PublishAllBody): Promise<PublishAllResponse> {
  return publishAllResponseSchema.parse(await methods.Post('agent_sessions/publish_all', body));
}

const agentSessionSettingsSchema = z.object({
  auto_publish: z.boolean(),
  /** Unpublished sessions publish-all publishes by default: not the ones the owner made private. */
  unpublished_session_count: z.number(),
  /**
   * Unpublished sessions the owner made private. Absent from a server that cannot
   * skip them, whose publish-all publishes them with the rest.
   */
  kept_private_session_count: z.number().optional(),
});
export type AgentSessionSettings = z.infer<typeof agentSessionSettingsSchema>;

/**
 * The signed-in user's agent-session sharing settings in this workspace.
 * GET /:account_id/agent_session_settings
 */
export async function getAgentSessionSettings(
  get: (path: string) => Promise<unknown> = path => methods.Get(path)
): Promise<AgentSessionSettings> {
  return agentSessionSettingsSchema.parse(await get('agent_session_settings'));
}

/**
 * Publish new sessions automatically from now on; sessions already uploaded
 * keep their state.
 * PATCH /:account_id/agent_session_settings
 */
export async function updateAgentSessionSettings(body: {
  auto_publish: boolean;
}): Promise<AgentSessionSettings> {
  return agentSessionSettingsSchema.parse(await methods.Patch('agent_session_settings', body));
}

const projectAutomationSchema = z.object({
  /** The owner opted in; covers activity from `started_at` on. */
  enabled: z.boolean(),
  /** Rails assigns confident predictions itself; without it, they are only suggested. */
  auto_apply_enabled: z.boolean().optional(),
  started_at: z.string().nullable(),
  daily_limit: z.number(),
});
export type ProjectAutomation = z.infer<typeof projectAutomationSchema>;

/**
 * The signed-in user's background project predictions for new agent periods.
 * GET /:account_id/agent_sessions/project_automation
 */
export async function getProjectAutomation(): Promise<ProjectAutomation> {
  return projectAutomationSchema.parse(await methods.Get('agent_sessions/project_automation'));
}

/**
 * Turn background project predictions on or off; on applies to activity from now on.
 * PATCH /:account_id/agent_sessions/project_automation
 */
export async function updateProjectAutomation(enabled: boolean): Promise<ProjectAutomation> {
  return projectAutomationSchema.parse(
    await methods.Patch('agent_sessions/project_automation', { enabled })
  );
}
